import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CardModelV2 } from 'shared';
import './ui.css';
import { Card } from './Card';

/*
 * Hand — a fanned set of cards for one seat. One component for BOTH the local
 * face-up interactive hand (pass `cards` + onPlay + isTurn) and the face-down
 * opponent hands (pass `count` + faceDown). Replaces PlayerOneHandSixCards and
 * OpponentHand. The parent Seat positions and rotates the whole fan; this lays
 * out cards around its own center.
 *
 * Two bits of motion, both on the inner wrapper so they compose with the fan's
 * static rotation on the outer one:
 *
 *   1. Deal-in: when cards are added, the cards drop in from the table center
 *      three at a time, the way a hand is actually dealt, rather than appearing
 *      all at once.
 *   2. Play-out: the card named by `playingCardId` lifts toward the table and
 *      fades, so the play is one motion continuing into TrickCard's fly-in
 *      instead of the card blinking out of the fan.
 */

/** Cards dealt in one pass, and the beat between passes. */
const DEAL_PACKET_SIZE = 3;
const DEAL_PACKET_MS = 200;

interface HandProps {
  cardW: number;
  cardH: number;
  fanSpreadDeg: number;
  /** 0..1: how much adjacent cards overlap horizontally. */
  overlap: number;
  faceDown?: boolean;
  /** Face-up cards (local hand). */
  cards?: CardModelV2[];
  /** Face-down card count (opponent hand). */
  count?: number;
  /** Enable click-to-play on face-up cards. */
  isTurn?: boolean;
  onPlay?: (cardId: string) => void;
  /** If set, only these card ids are highlighted/playable; otherwise all. */
  legalCardIds?: Set<string>;
  /** Card ids to flag as unplayable (red outline). */
  illegalCardIds?: Set<string>;
  /** Card the player just played: lifts out of the fan toward the table. */
  playingCardId?: string;
}

export const Hand: React.FC<HandProps> = ({
  cardW,
  cardH,
  fanSpreadDeg,
  overlap,
  faceDown,
  cards,
  count,
  isTurn,
  onPlay,
  legalCardIds,
  illegalCardIds,
  playingCardId,
}) => {
  const k = faceDown ? (count ?? 0) : (cards?.length ?? 0);

  // Re-run the deal-in stagger only when cards are added (a fresh hand), not on
  // every play. Bumping the sequence re-keys the cards, which restarts the CSS
  // animation.
  const [dealSeq, setDealSeq] = useState(0);
  const previousCount = useRef(k);
  useEffect(() => {
    if (k > previousCount.current) setDealSeq((seq) => seq + 1);
    previousCount.current = k;
  }, [k]);

  if (k <= 0) return null;

  const step = cardW * (1 - overlap);
  const totalW = step * (k - 1) + cardW;
  const center = (k - 1) / 2;
  const perCardDeg = k > 1 ? fanSpreadDeg / (k - 1) : 0;

  // Lay the cards along a fan arc rather than a flat line. Each card rotates
  // about its own bottom-center, so if the bottom-centers sat on a flat line the
  // outer (tilted) cards would swing their inner top corners up above the middle
  // cards. Instead we place every card's bottom-center on a common circle of
  // radius R below the fan, so the whole hand curves smoothly and the outer
  // cards dip to follow the arc. R is derived from the horizontal step so the
  // fan keeps the same width/overlap it had before.
  const perCardRad = (perCardDeg * Math.PI) / 180;
  const R = perCardDeg > 0 ? step / Math.sin(perCardRad) : 0;
  const pivotX = totalW / 2;

  return (
    <div style={{ position: 'relative', width: totalW, height: cardH }}>
      {Array.from({ length: k }).map((_, i) => {
        const tilt = (i - center) * perCardDeg;
        const tiltRad = (tilt * Math.PI) / 180;
        // Bottom-center of this card on the shared arc; falls back to flat linear
        // spacing for a single card (R === 0).
        const bottomX = R > 0 ? pivotX + R * Math.sin(tiltRad) : i * step + cardW / 2;
        const bottomY = R > 0 ? cardH + R * (1 - Math.cos(tiltRad)) : cardH;
        const left = bottomX - cardW / 2;
        const top = bottomY - cardH;
        const card = !faceDown ? cards![i] : undefined;
        // Playable on your turn (hover-lift + click); the gold "legal" outline is
        // reserved for known legal moves, if the caller provides them.
        const playable = !!card && !!isTurn;
        const legal = !!card && !!legalCardIds && legalCardIds.has(card.id);
        const illegal = !!card && !!illegalCardIds && illegalCardIds.has(card.id);

        const playing = !!card && card.id === playingCardId;

        return (
          <div
            key={`${dealSeq}:${card?.id ?? i}`}
            style={{
              position: 'absolute',
              left,
              top,
              transform: `rotate(${tilt}deg)`,
              transformOrigin: '50% 100%',
            }}
          >
            <div
              className="ui-hand-card"
              style={{
                animationDelay: `${Math.floor(i / DEAL_PACKET_SIZE) * DEAL_PACKET_MS}ms`,
                transform: playing ? 'translateY(-160%) scale(0.85)' : undefined,
                opacity: playing ? 0 : undefined,
              }}
            >
              <Card
                code={card?.imageUrl}
                faceDown={faceDown}
                width={cardW}
                height={cardH}
                interactive={playable}
                legal={legal}
                illegal={illegal}
                onClick={card && onPlay ? () => onPlay(card.id) : undefined}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Hand;
